import BuildIcon from '@mui/icons-material/Build';
import FavoriteIcon from '@mui/icons-material/Favorite';
import HeartBrokenIcon from '@mui/icons-material/HeartBroken';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import PersonIcon from '@mui/icons-material/Person';
import WarningIcon from '@mui/icons-material/Warning';
import { Box, ListItem, Tooltip, Typography } from '@mui/material';
import HoverPopover from 'material-ui-popup-state/HoverPopover';
import { bindHover, bindPopover, usePopupState } from 'material-ui-popup-state/hooks';
import React, { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { ListChildComponentProps } from 'react-window';

import { DeviceV3, Malfunction } from '../../../backendsdk';
import { TrackedListItemButton as ListItemButton } from '../../../components/TrackedComponents';
import useDrivers from '../../../hooks/drivers';
import useIsMobile from '../../../hooks/isMobile';
import useProfile from '../../../hooks/profile';
import { formatTime } from '../../../utils/TimeFormatter';
import palette from '../../ColorPalette';
import { RTLDirectionContext } from '../../Layout';
import WellnessPanel, { WELLNESS_TO_ACTION_MAP } from './DeviceDetails/WellnessPanel';
import { getFFWellnessDetails, getICWellnessDetails, getWellnessIconColor } from './utils';
import { RELEVANT_EVENT_TYPES } from './utils';

export const ONLINE_DEVICE_HEIGHT = 120;
export const OFFLINE_DEVICE_HEIGHT = 72;

interface ListRowData {
    devices: DeviceV3[];
    setHoveredDeviceId: CallableFunction;
    now: Date;
    malfunctionsByDeviceId: Record<string, Malfunction[]>;
}

const DeviceListRow: React.FC<ListChildComponentProps<ListRowData>> = (props) => {
    const { index, style } = props;
    const device = props.data.devices[index];
    const { t } = useTranslation();
    const isRTL = useContext(RTLDirectionContext);
    const { profile } = useProfile();
    const history = useHistory();
    const isMobile = useIsMobile();
    const { getDriver } = useDrivers();
    const malfunctions = props.data.malfunctionsByDeviceId[device.device.device_id] || [];

    const icWellnessDetails = getICWellnessDetails(device, profile.customer.settings.inactive_device_threshold);
    const ffWellnessDetails = getFFWellnessDetails(device, profile.customer.settings.inactive_device_threshold);
    const wellnessColor = getWellnessIconColor(icWellnessDetails, ffWellnessDetails);
    const WellnessIcon = wellnessColor === palette.wellness.red ? HeartBrokenIcon : FavoriteIcon;

    const driver = getDriver(device.current_trip?.driver);
    const driverName = driver && !driver.is_auto_generated ? driver.fullName : undefined;

    const timeStringPrefix = !!device.current_trip
        ? `${t('content.fleet.current_trip')}:`
        : `${t('content.fleet.last_seen')}:`;
    const timeString = !!device.current_trip
        ? formatTime(props.data.now.getTime() / 1000 - device.current_trip.start_time, t, true)
        : !!device.last_seen
        ? t('time.ago', {
              time: formatTime(props.data.now.getTime() / 1000 - device.last_seen, t, true),
          })
        : t('content.fleet.unknown');

    let eventsField = null;
    if (!!device.current_trip) {
        let value;
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        const relevantEvents = device.current_trip.number_of_events_per_type!.filter((event) =>
            RELEVANT_EVENT_TYPES.includes(event.event_type),
        );
        const eventCount = relevantEvents.reduce((acc, event) => acc + event.count, 0);
        if (eventCount > 0) {
            value = eventCount === 1 ? t('content.fleet.event') : t('content.fleet.events', { count: eventCount });
        } else {
            value = t('content.fleet.no_events');
        }
        eventsField = (
            <Box
                id={`events-${device.device.license_plate}`}
                sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start' }}
            >
                <WarningIcon
                    fontSize="small"
                    sx={{ width: 28, color: eventCount === 0 ? 'wellnessGray.main' : palette.red[700] }}
                />
                <Typography
                    id={`events-${device.device.device_id}`}
                    sx={{ color: eventCount === 0 ? 'wellnessGray.main' : palette.red[700] }}
                >
                    {value}
                </Typography>
            </Box>
        );
    }

    const popupState = usePopupState({
        variant: 'popover',
        popupId: 'wellness-popover',
    });

    const hasRequiredActions =
        !!WELLNESS_TO_ACTION_MAP[icWellnessDetails] ||
        !!WELLNESS_TO_ACTION_MAP[ffWellnessDetails as keyof typeof WELLNESS_TO_ACTION_MAP];

    return (
        <ListItem
            className="device-list-item"
            data-testid="device-list-item"
            style={style}
            key={device.device.device_id}
            disablePadding
            disableGutters
            sx={{
                borderBottomWidth: 1,
                borderBottomStyle: 'solid',
                borderBottomColor: 'lightGrayColor.main',
                direction: 'ltr',
                height: !!device.current_trip ? ONLINE_DEVICE_HEIGHT : OFFLINE_DEVICE_HEIGHT,
            }}
            onMouseEnter={() => props.data.setHoveredDeviceId(device.device.device_id)}
            onMouseLeave={() => props.data.setHoveredDeviceId(undefined)}
        >
            <ListItemButton
                id={`vehicle-list-item-${device.device.device_id}`}
                data-testid={`vehicle-list-item-${device.device.device_id}`}
                onClick={() => {
                    props.data.setHoveredDeviceId(undefined);
                    history.push(`/overview/${encodeURIComponent(device.device.license_plate)}`);
                }}
            >
                <Box
                    sx={{
                        width: '100%',
                        maxWidth: '100%',
                        display: 'flex',
                        justifyContent: 'space-between',
                        py: 1,
                        position: 'relative',
                    }}
                >
                    <Box
                        sx={{
                            width: '100%',
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'center',
                            gap: 1,
                        }}
                    >
                        <Box
                            id={`license-plate-${device.device.license_plate}`}
                            sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start' }}
                        >
                            <LocalShippingIcon fontSize="small" sx={{ width: 28, color: 'primary.main' }} />
                            <Typography
                                data-testid="list-item-license-plate"
                                sx={{
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                    maxWidth: '100%',
                                }}
                            >
                                {device.device.license_plate}
                            </Typography>
                        </Box>
                        {!!device.current_trip ? (
                            <Box
                                id={`driver-name-${device.device.license_plate}`}
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'flex-start',
                                }}
                            >
                                <PersonIcon
                                    fontSize="small"
                                    sx={{
                                        width: 28,
                                        color: !!driverName ? 'primary.main' : 'wellnessGray.main',
                                    }}
                                />
                                <Typography
                                    data-testid="list-item-driver-name"
                                    sx={{
                                        color: !!driverName ? undefined : palette.neutral[700],
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                    }}
                                >
                                    {`${!!driverName ? driverName : t('content.fleet.unknown_driver')}${
                                        !!driver?.sub_fleet ? ` (${driver?.sub_fleet})` : ''
                                    }`}
                                </Typography>
                            </Box>
                        ) : null}
                        {eventsField}
                    </Box>
                    <Box
                        sx={{
                            width: '100%',
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'flex-start',
                            alignItems: 'flex-end',
                            gap: 1,
                        }}
                    >
                        <Box>
                            <Typography
                                sx={{ fontSize: 12, lineHeight: '12px' }}
                            >{`${timeStringPrefix} ${timeString}`}</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            {malfunctions.length > 0 ? (
                                <Tooltip
                                    title={
                                        malfunctions.length === 1
                                            ? t('content.fleet.malfunction')
                                            : t('content.fleet.malfunctions', { count: malfunctions.length })
                                    }
                                    arrow
                                >
                                    <Box
                                        id={`${device.device.device_id}-malfunctions`}
                                        data-testid={`${device.device.device_id}-malfunctions`}
                                        data-malfunction-count={malfunctions.length}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            history.push(
                                                `/tasks?license_plate=${encodeURIComponent(
                                                    malfunctions[0].license_plate,
                                                )}`,
                                            );
                                        }}
                                    >
                                        <BuildIcon fontSize="small" sx={{ color: palette.wellness.red, mr: 1 }} />
                                    </Box>
                                </Tooltip>
                            ) : null}
                            <Box {...bindHover(popupState)} sx={{ display: 'flex', alignItems: 'flex-start' }}>
                                <WellnessIcon fontSize="small" sx={{ color: wellnessColor }} />
                            </Box>
                        </Box>
                        <HoverPopover
                            {...bindPopover(popupState)}
                            anchorOrigin={{
                                vertical: 'center',
                                horizontal: isRTL ? 'left' : 'right',
                            }}
                            transformOrigin={{
                                vertical: 'center',
                                horizontal: isRTL ? 'right' : 'left',
                            }}
                        >
                            <Box
                                sx={{
                                    width: isMobile ? '225px' : '430px',
                                    p: 1,
                                    pb: hasRequiredActions ? 0.5 : 1,
                                }}
                            >
                                <WellnessPanel device={device} />
                            </Box>
                        </HoverPopover>
                    </Box>
                </Box>
            </ListItemButton>
        </ListItem>
    );
};

export default DeviceListRow;
